import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Button, Group, Switch, Table, Text, TextInput } from '@mantine/core';
import {
  useIsyeriAc,
  useLokasyonAc,
  useSirketAc,
  useSirketGuncelle,
  useSirketSil,
  useLokasyonToplanmaNoktasi,
} from '../api/queries';
import type { LocationNodeSummary } from '@isg/shared-types';
import { SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * ORGANİZASYON YAZMA YÜZEYİ — **TEK YAZMA YOLU** (TUR 9 / B1).
 *
 * > **On dört uç kırk bir gün ekransız durdu** (T9-01, h-3'ün rekoru): şirket
 * > açma · işyeri açma · lokasyon açma · arşiv geçişleri uçları F1·S1'den
 * > beri duruyordu ve **hiçbir ekran onları çağırmıyordu** — yeni bir müşteri
 * > sisteme ancak geliştirici eliyle, seed'le alınabiliyordu.
 *
 * ⚠️ **YAZMA KANCALARINI YALNIZ BU DOSYA ÇAĞIRIR** (`useSirketAc` ·
 * `useSirketGuncelle` · `useSirketSil` · `useIsyeriAc` · `useLokasyonAc`).
 * Kural `yetki-parts`ın (Y1) aynısıdır ve **kod denetimiyle** kilitlidir:
 * ikinci bir yol, ikinci doğrulama kümesi ve zamanla ayrışan iki davranış
 * demektir (emsal §18.1/P1 — *eşleştirme kanal başına yazılmaz*).
 *
 * ⚠️ **SÖZLEŞME (P1 · P1-a):** işyeri **kendi sayfası olan** bir kayıttır
 * (künye + lokasyon ağacı + durum), lokasyon ise **alt kalemdir** → satır içi
 * SOLUK SATIRLA girilir. Şirket de satır içi açılır: adı olan bir kayıttır,
 * kendi sayfasını hak etmez.
 *
 * ⚠️ **BUTON GÖRÜNÜRLÜĞÜ ≠ YETKİ** (§2.16): karar her istekte guard'dadır;
 * ekran yalnız görünümü sadeleştirir. Bahçe dışı hedef **403** döner ve
 * mesaj kullanıcıya **olduğu gibi** gösterilir (D2: sunucu zaten sızdırmaz
 * dille konuşur).
 */

/** ✏️ TUR 9 / K7: şirketin SGK sicili YOK — sicil işyerinin künyesidir. */
export function SirketEkleSatiri({
  testId = 'sirket-ekle',
}: {
  testId?: string;
}): React.JSX.Element {
  const [ad, setAd] = useState('');
  const olustur = useSirketAc();
  const kaydet = (): void => {
    if (ad.trim() === '') return;
    olustur.mutate({ ad: ad.trim() }, { onSuccess: () => setAd('') });
  };
  return (
    <Group align="flex-end" gap="sm" data-testid={testId}>
      <TextInput
        label="Yeni şirket"
        placeholder="Şirket adı"
        value={ad}
        onChange={(e) => setAd(e.currentTarget.value)}
        data-testid={`${testId}-ad`}
        w={320}
      />
      <Button
        onClick={kaydet}
        disabled={ad.trim() === ''}
        loading={olustur.isPending}
        data-testid={`${testId}-kaydet`}
      >
        Şirket ekle
      </Button>
      <YazmaHatasi hata={olustur.error} testId={`${testId}-hata`} />
    </Group>
  );
}

export function SirketAdiDuzenle({
  companyId,
  mevcutAd,
  onKapat,
}: {
  companyId: string;
  mevcutAd: string;
  onKapat: () => void;
}): React.JSX.Element {
  const [ad, setAd] = useState(mevcutAd);
  const guncelle = useSirketGuncelle();
  return (
    <Group align="flex-end" gap="sm" data-testid="sirket-duzenle">
      <TextInput
        label="Şirket adı"
        value={ad}
        onChange={(e) => setAd(e.currentTarget.value)}
        data-testid="sirket-duzenle-ad"
        w={320}
      />
      <Button
        onClick={() => guncelle.mutate({ id: companyId, ad: ad.trim() }, { onSuccess: onKapat })}
        disabled={ad.trim() === '' || ad.trim() === mevcutAd}
        loading={guncelle.isPending}
        data-testid="sirket-duzenle-kaydet"
      >
        Kaydet
      </Button>
      <Button variant="default" onClick={onKapat}>
        Vazgeç
      </Button>
      <YazmaHatasi hata={guncelle.error} testId="sirket-duzenle-hata" />
    </Group>
  );
}

/**
 * Şirket silme — **fren SUNUCUDADIR** (silinmemiş işyeri varsa 409, arşivli
 * dahil). Ekran freni tekrar yazmaz; reddi olduğu gibi gösterir (§2.16).
 */
export function useSirketSilme(): {
  sil: (companyId: string) => void;
  bekliyor: boolean;
  hata: Error | null;
} {
  const sil = useSirketSil();
  return {
    sil: (companyId) => sil.mutate({ id: companyId }),
    bekliyor: sil.isPending,
    hata: sil.error,
  };
}

/**
 * İŞYERİ EKLEME — satır içi (P1-a). ⚠️ **TEHLİKE SINIFI SORULMAZ**: NACE
 * kodundan TÜRETİLİR ve girdi olarak kabul EDİLMEZ (Kapsam §3.2; fail-loud —
 * eşlemede olmayan kodla işyeri açılamaz, 400). Ekranda bir "tehlike sınıfı"
 * seçici çizmek, kullanıcıya sistemin kararını **ezebileceğini** söylerdi.
 */
export function IsyeriEkleSatiri({ companyId }: { companyId: string }): React.JSX.Element {
  const [ad, setAd] = useState('');
  const [sgkSicil, setSgkSicil] = useState('');
  const [naceKod, setNaceKod] = useState('');
  const olustur = useIsyeriAc();
  const dolu = ad.trim() !== '' && sgkSicil.trim() !== '' && naceKod.trim() !== '';
  return (
    <Group align="flex-end" gap="sm" data-testid={`isyeri-ekle-${companyId}`}>
      <TextInput
        label="Yeni işyeri"
        placeholder="İşyeri adı"
        value={ad}
        onChange={(e) => setAd(e.currentTarget.value)}
        data-testid="isyeri-ekle-ad"
        w={220}
      />
      <TextInput
        label="SGK sicil"
        value={sgkSicil}
        onChange={(e) => setSgkSicil(e.currentTarget.value)}
        data-testid="isyeri-ekle-sicil"
        w={160}
      />
      <TextInput
        label="NACE kodu"
        description="Tehlike sınıfı bundan türetilir"
        value={naceKod}
        onChange={(e) => setNaceKod(e.currentTarget.value)}
        data-testid="isyeri-ekle-nace"
        w={200}
      />
      <Button
        onClick={() =>
          olustur.mutate(
            { companyId, ad: ad.trim(), sgkSicil: sgkSicil.trim(), naceKod: naceKod.trim() },
            {
              onSuccess: () => {
                setAd('');
                setSgkSicil('');
                setNaceKod('');
              },
            },
          )
        }
        disabled={!dolu}
        loading={olustur.isPending}
        data-testid="isyeri-ekle-kaydet"
      >
        İşyeri ekle
      </Button>
      <YazmaHatasi hata={olustur.error} testId="isyeri-ekle-hata" />
    </Group>
  );
}

/**
 * LOKASYON AĞACI — liste + satır içi ekleme (P1-a). Düğüm bir **alt
 * kalemdir**: kendi sayfası açılmaz.
 *
 * ⚠️ **ARŞİVLİ İŞYERİNDE YAZMA KONTROLÜ ÇİZİLMEZ** (§2.40'ın 2. seçeneği:
 * çalışamayacak kontrol görünmez olur). Kapı sunucudadır ve 409 döner; ekran
 * onu ÖNLER, yerine geçmez.
 */
export function LokasyonBolumu({
  workplaceId,
  dugumler,
  yazilabilir,
}: {
  workplaceId: string;
  dugumler: readonly LocationNodeSummary[];
  yazilabilir: boolean;
}): React.JSX.Element {
  const [ad, setAd] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  // P12 (madde 224) — kaydedilmemiş lokasyon satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(ad !== '' || parentId !== null ? 1 : 0, 'lokasyon satırı');
  const olustur = useLokasyonAc();
  // ACİL DURUM / E4 (Kapsam §29): toplanma noktası ağaca İŞARETTİR — yazma
  // kapısı lokasyon eklemeyle aynı; arşivli işyerinde anahtar çizilmez.
  const isaretle = useLokasyonToplanmaNoktasi();
  return (
    <>
      <Table striped data-testid="lokasyon-listesi">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Düğüm</Table.Th>
            <Table.Th>Tip</Table.Th>
            <Table.Th>Üst düğüm</Table.Th>
            <Table.Th w={160}>Toplanma noktası</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {dugumler.map((dugum) => (
            <Table.Tr key={dugum.id} data-testid="lokasyon-satiri">
              <Table.Td>{dugum.ad}</Table.Td>
              <Table.Td>{dugum.tip === 'permanent' ? 'Kalıcı' : 'Geçici'}</Table.Td>
              <Table.Td>{dugumler.find((u) => u.id === dugum.parentId)?.ad ?? '— (kök)'}</Table.Td>
              <Table.Td>
                {yazilabilir ? (
                  <Switch
                    size="xs"
                    aria-label="Toplanma noktası"
                    data-testid={`lokasyon-toplanma-${dugum.id}`}
                    checked={dugum.toplanmaNoktasi}
                    onChange={(e) => {
                      // D1: değer güncelleyiciden ÖNCE okunur (currentTarget olay bitince boşalır).
                      const toplanmaNoktasi = e.currentTarget.checked;
                      isaretle.mutate({ workplaceId, locationId: dugum.id, toplanmaNoktasi });
                    }}
                  />
                ) : dugum.toplanmaNoktasi ? (
                  <Text size="xs" data-testid={`lokasyon-toplanma-${dugum.id}`}>
                    Toplanma noktası
                  </Text>
                ) : (
                  <Text size="xs" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {yazilabilir && (
            <SolukSatir
              testId="lokasyon-ekle-satiri"
              kolonSayisi={4}
              dokunuldu={ad !== '' || parentId !== null}
              kaydedilebilir={ad.trim() !== ''}
              kaydediliyor={olustur.isPending}
              onKaydet={() =>
                olustur.mutate(
                  { workplaceId, ad: ad.trim(), tip: 'permanent', parentId },
                  {
                    onSuccess: () => {
                      setAd('');
                      setParentId(null);
                    },
                  },
                )
              }
            >
              <Table.Td>
                <TextInput
                  placeholder="Yeni düğüm adı"
                  value={ad}
                  onChange={(e) => setAd(e.currentTarget.value)}
                  data-testid="lokasyon-ekle-ad"
                />
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  Kalıcı
                </Text>
              </Table.Td>
              <Table.Td>
                <select
                  value={parentId ?? ''}
                  onChange={(e) => setParentId(e.currentTarget.value || null)}
                  data-testid="lokasyon-ekle-parent"
                  style={{ minWidth: 160 }}
                >
                  <option value="">— (kök)</option>
                  {dugumler.map((dugum) => (
                    <option key={dugum.id} value={dugum.id}>
                      {dugum.ad}
                    </option>
                  ))}
                </select>
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      <YazmaHatasi hata={olustur.error} testId="lokasyon-ekle-hata" />
      <YazmaHatasi hata={isaretle.error} testId="lokasyon-toplanma-hata" />
    </>
  );
}

/**
 * ⚠️ **RET MESAJI OLDUĞU GİBİ GÖSTERİLİR.** Sunucu D2 dilinde konuşur
 * (§2.6c · §2.40): *"İşyeri bahçenizin dışında"* · *"Şirketin silinmemiş
 * işyeri kaydı var"*. Ekranın onu kendi cümlesiyle değiştirmesi, kullanıcıya
 * **sebebi olmayan bir ret** göstermek olurdu (§2.40'ın en kötü kolu).
 */
export function YazmaHatasi({
  hata,
  testId,
}: {
  hata: Error | null;
  testId: string;
}): React.JSX.Element | null {
  if (!hata) return null;
  return (
    <Text size="xs" c={ANLAM.engel} role="alert" data-testid={testId}>
      {hata.message}
    </Text>
  );
}
